import { STATION_PAIR_VERDICTS, getStationVerdictForPair } from '../laneEligibility';

/**
 * Reshape one station row into the `routes` entry `laneEligibility` expects.
 *
 * The editor works on ONE lane, so the array is always length 1 — but the
 * shape has to match what the lane map builds, because the same verdict
 * functions read it. The city names come from the lane, not the station row:
 * the row carries distances only.
 */
const toRouteMeasurements = (stationRow, lane) => [
  {
    distanceToPolylineMeters: stationRow.distanceToPolylineMeters,
    distanceToOriginCityMeters: stationRow.distanceToOriginCityMeters,
    distanceToDestinationCityMeters: stationRow.distanceToDestinationCityMeters,
    originCityName: lane.originCityName,
    destinationCityName: lane.destinationCityName,
  },
];

const isEligible = (stationRow, lane, limits) =>
  getStationVerdictForPair(stationRow, toRouteMeasurements(stationRow, lane), limits) ===
  STATION_PAIR_VERDICTS.ELIGIBLE;

/**
 * Which stations gain or lose eligibility if the candidate geometry is saved.
 *
 * Verdicts are computed HERE, from raw distances, using the same
 * `laneEligibility` functions the lane map uses. The backend deliberately
 * returns distances and no verdicts: one copy of the rules means the preview
 * cannot quietly stop predicting the real outcome when a limit changes.
 *
 * A station absent from one side is treated as not eligible there rather than
 * skipped. The preview radius is 25 km around each line, so a big enough move
 * drops stations out of one set entirely — and "it disappeared" is exactly
 * the case where it also stopped being bookable.
 */
export const diffStationVerdicts = ({ currentStations, candidateStations, lane, limits }) => {
  const currentById = new Map(currentStations.map((s) => [s.id, s]));
  const candidateById = new Map(candidateStations.map((s) => [s.id, s]));
  const allIds = new Set([...currentById.keys(), ...candidateById.keys()]);

  const gained = [];
  const lost = [];

  allIds.forEach((id) => {
    const current = currentById.get(id);
    const candidate = candidateById.get(id);

    const wasEligible = current ? isEligible(current, lane, limits) : false;
    const willBeEligible = candidate ? isEligible(candidate, lane, limits) : false;

    if (!wasEligible && willBeEligible) gained.push(candidate);
    if (wasEligible && !willBeEligible) lost.push(current);
  });

  return { gained, lost };
};
